import { useEffect, useRef, useState } from "react";
import { enterWithToken, fetchSession } from "../lib/captureClient";
import { productDiffers } from "../lib/linkDecision";

// Redeeming the handoff link — the two seconds between tapping a link in the
// Shopify admin and standing in front of a shelf.
//
// ---------------------------------------------------------------------------
// REDEMPTION IS A POST, AND IT HAPPENS EXACTLY ONCE
// ---------------------------------------------------------------------------
// The token is single-use server-side, so a second attempt with the same one
// fails — correctly, and permanently. That makes React 18's StrictMode, which
// mounts every component twice in development, a genuine hazard here rather
// than a curiosity: the first mount would spend the token and the second would
// report it dead. `spent` guards against that, and against a double tap.
//
// It is a POST rather than a GET because the whole point is to move the token
// out of the URL and into a cookie the page cannot read. A GET would put it in
// the backend's access log on the way.

export default function Enter({ token, onEntered, expect = {} }) {
  const [error, setError] = useState(false);
  const spent = useRef(false);

  useEffect(() => {
    if (spent.current) return;
    spent.current = true;

    (async () => {
      try {
        await enterWithToken(token);
        await onEntered();
      } catch {
        // BEFORE reporting a failure: are we already in? A reload with the
        // token still in the address bar re-POSTs a token this very device
        // spent seconds ago, and the server correctly refuses it — but the
        // session it created is sitting right there in a cookie. Showing the
        // door then would lock someone out of a session they already hold.
        //
        // ⚠️ IN, TO THE RIGHT PLACE. "A session exists" is not the same claim
        // as "the session this link was for", and treating them as one is how
        // a Krystal link that arrived a minute too late dropped someone back
        // into variety's pile reporting success. The caller passes what the
        // link promised; if the live session does not match it, this is a dead
        // link and it says so, rather than quietly honouring a different one.
        try {
          const live = await fetchSession();
          if (live) {
            // The binding compares absent-as-none, like linkDecision: a
            // product link must land in THAT product, a barcode link in THAT
            // barcode, a shelf link in neither. Same function App uses, so
            // the two cannot disagree about what "the same session" means.
            const mismatched =
              (expect.shop && live.shop !== expect.shop) ||
              (expect.mode && live.mode !== expect.mode) ||
              (("product" in expect || "barcode" in expect || "newItem" in expect) &&
                productDiffers(live, {
                  product: expect.product,
                  barcode: expect.barcode,
                  newItem: expect.newItem,
                  grant: expect.grant,
                }));
            if (!mismatched) return void onEntered();
          }
        } catch {
          /* no session either; fall through to the message below */
        }

        // ⚠️ SAY WHAT TO DO, NOT WHAT WENT WRONG. The server deliberately
        // answers every refusal identically — expired, already redeemed,
        // revoked, shop switched off — so this page genuinely does not know
        // which it was. An earlier version guessed ("expired or already
        // used"), and the first person to hit it read the second half and
        // reported that someone else had taken their link. Naming a cause we
        // cannot know turns an expired link into a security scare.
        setError(true);
      }
    })();
  }, [token, onEntered]);

  if (error) {
    return (
      <div className="cap-door">
        <h1>This link no longer works</h1>
        <p>Ask your store admin to send a new one.</p>
        <p className="cap-door-note">
          Capture links are good for five minutes and open on one device. Once a
          device is set up it stays signed in for up to twelve hours, or thirty
          minutes for a link from one product or one Quick Scan.
        </p>
      </div>
    );
  }

  return (
    <div className="cap-door" aria-busy="true">
      <h1>Signing in…</h1>
      <p>Setting this device up to capture.</p>
    </div>
  );
}
